import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { Field, Fields, reduxForm, SubmissionError } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';
import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';

import DatepickerField from 'components/fields/DatepickerField';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import AutosuggestField from 'components/fields/AutosuggestField';
import UploadField from 'components/fields/UploadField';
import FormError from 'components/common/FormError';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';

import {
  createUser,
  updateUser,
  addUserResource,
  removeUserResource,
  generateUserActivityLog,
} from 'redux/modules/users';
import { accountResetPassword, accountLoginFail } from 'redux/modules/account';

import {
  requestFetchRoles,
  requestSearchRoles,
} from 'redux/modules/roles';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';
import groupAccessPermissions from 'utils/groupAccessPermissions';

import userTitles from 'config/userTitles';
import imageFileTypes from 'config/imageFileTypes';
import dateFormat from 'config/dateFormat';

import UserCreateEditFormSection from './UserCreateEditFormSection';

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridItemRecoveryLink: {
    marginTop: 26,
  },
  button: {
    marginLeft: 20,
  },
  form: {
    width: 730,
    margin: '10px auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  gridItemShrink: {
    flexShrink: 1,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {
      ...formValues.Active === 'true'
        && { Active: true },
      ...formValues.Active === 'false'
        && { Active: false },
      ...formValues.Role
        && { 'Roles ID': values.Role.map(item => item.value) },
    });
    delete normalizedValues['Access Level'];
    delete normalizedValues.Role;
    delete normalizedValues['Selected Role'];
    delete normalizedValues['Activity Log From'];
    delete normalizedValues['Activity Log To'];
    return normalizedValues;
  };

  if (props.initialValues.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    delete diffValues.Role;
    const normalizedValues = {
      ...normalizeValues(diffValues),
      ...diffValues.Image && { Image: values.Image },
    };
    const addedRole = values.Role
      .filter(item =>
        !props.initialValues.Role.find(initialItem => initialItem.value === item.value))
      .map(item => item.value);
    const removedRole = props.initialValues.Role
      .filter(item =>
        !values.Role.find(currentItem => currentItem.value === item.value))
      .map(item => item.value);
    return new Promise((resolve, reject) => {
      if (Object.keys(normalizedValues).length > 0) {
        dispatch(updateUser({
          values: {
            ID: props.initialValues.ID,
            ...normalizedValues,
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
      .then(() => new Promise((resolve, reject) => {
        if (addedRole.length > 0) {
          dispatch(addUserResource({
            values: {
              ID: props.initialValues.ID,
              'Roles ID': addedRole,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedRole.length > 0) {
          dispatch(removeUserResource({
            values: {
              ID: props.initialValues.ID,
              'Roles ID': removedRole,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(res => res,
        (err) => {
          if (err.error) {
            throw new SubmissionError({ _error: err.error });
          }
          throw new SubmissionError(err);
        });
  }
  return new Promise((resolve, reject) => {
    dispatch(createUser({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function onChange(values, dispatch, props) {
  if (values['Selected Role']) {
    if (!values.Role
      || values.Role.findIndex(item =>
        item.value === values['Selected Role'].value) === -1) {
      props.array.push('Role', values['Selected Role']);
    }
    props.clearFields(false, false, 'Selected Role');
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values.Title && userTitles.indexOf(values.Title) === -1) {
    errors.Title = 'Please, select Title from list';
  }

  if (!values.Role || values.Role.length === 0) {
    errors['Role Name'] = 'Please, select Role from list';
  }

  return errors;
};

const UserCreateEditForm = ({
  submit,
  handleSubmit,
  submitting,
  error,
  actions,
  initialValues,
  formValues,
  change,
  account,
  accessPermissions,
  classes,
}) => {
  const groupedAccessPermissions = groupAccessPermissions(
    accessPermissions.map((item) => {
      if (formValues.Role
        && formValues.Role.find(rolesItem => rolesItem.accesses.indexOf(item.ID) !== -1)) {
        return {
          ...item,
          active: true,
        };
      }
      return item;
    }),
  );
  const selectedRolesID = formValues.Role
    ? formValues.Role.map(item => item.value)
    : [];
  return (
    <Section>
      <SectionHeader fixed>
        <SectionTitle>
          {initialValues.ID ? 'Edit User' : 'Create User'}
        </SectionTitle>
        <div>
          <Button
            className={classes.button}
            variant="raised"
            href="/settings/users"
          >
            Cancel
          </Button>
          <Button
            className={classes.button}
            variant="raised"
            color="primary"
            disabled={submitting}
            onClick={submit}
          >
            {initialValues.ID ? 'Save' : 'Create'}
          </Button>
        </div>
      </SectionHeader>
      <SectionBody>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {error &&
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid
                item
                xs={12}
              >
                <FormError>
                  {error}
                </FormError>
              </Grid>
            </Grid>
          }
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
              md={7}
            >
              <Field
                id="Image"
                name="Image"
                component={UploadField}
                variant="image"
                minWidth={300}
                minHeight={300}
                cropWidth={300}
                cropHeight={300}
                maxSize={1000000}
                accept={imageFileTypes}
                label={(
                  <Fragment>
                    Drag file here <br />
                    JPG, PNG or GIF <br />
                    No larger than 1 MB <br />
                    Minimum 300x300 px
                  </Fragment>
                )}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid item xs={12} md={6}>
              <Field
                id="First Name"
                name="First Name"
                label="First Name *"
                component={TextField}
                validate={[validate.required]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                id="Last Name"
                name="Last Name"
                label="Last Name *"
                component={TextField}
                validate={[validate.required]}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            spacing={24}
            // wrap="nowrap"
          >
            <Grid item xs={4} md={3}>
              <Field
                id="Title"
                name="Title"
                label="Title *"
                component={AutosuggestField}
                validate={[validate.required]}
                placeholder="-Select Title-"
                fullWidth
                renderSuggestionContent={suggestion => suggestion}
                onSuggestionSelected={suggestion => suggestion}
                fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                  if (offset === 0) {
                    if (value.length > 0) {
                      resolve(userTitles
                        .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                    } else {
                      resolve(userTitles);
                    }
                  } else {
                    resolve([]);
                  }
                }))}
              />
            </Grid>
            <Grid item xs={8} md={4}>
              <FormControl role="group">
                <FormLabel component="legend">Gender *</FormLabel>
                <Field
                  name="Gender"
                  component={RadioGroupField}
                >
                  <RadioButtonField value="Male" label="Male" />
                  <RadioButtonField value="Female" label="Female" />
                  <RadioButtonField value="Other" label="Other" />
                </Field>
              </FormControl>
            </Grid>
            <Grid
              xs={12}
              md={4}
              className={classes.gridItemShrink}
              item
            >
              <FormControl component="fieldset">
                <FormLabel component="legend">User status *</FormLabel>
                <Field
                  name="Active"
                  component={RadioGroupField}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio color="primary" />}
                    label="Active"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio color="primary" />}
                    label="Inactive"
                  />
                </Field>
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid item xs={12} md={3}>
              <Field
                id="Mobile Phone"
                name="Mobile Phone"
                label="Mobile Phone *"
                component={TextField}
                validate={[validate.required, validate.phone]}
                format={value => (value ? value.replace('+1 ', '') : '')}
                normalize={normalize.phone}
                maxLength="14"
                startAdornment={
                  <InputAdornment position="start">
                    <TextFieldAdornmentText>
                      +1
                    </TextFieldAdornmentText>
                  </InputAdornment>
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                id="Email"
                name="Email"
                label="Email *"
                component={TextField}
                validate={[validate.required, validate.email]}
                fullWidth
              />
            </Grid>
            {(initialValues && initialValues.Email) &&
              <Grid
                className={classes.gridItemRecoveryLink}
                item
                xs={12}
                md={5}
              >
                <Button
                  variant="raised"
                  onClick={() => {
                    actions.accountResetPassword({
                      values: {
                        Email: initialValues.Email,
                      },
                    });
                  }}
                >
                  Send password recovery email
                </Button>
              </Grid>
            }
          </Grid>
          <Grid
            className={classes.grid}
            container
            justify="flex-start"
            wrap="nowrap"
            spacing={24}
          >
            <Grid item xs={12} md={7}>
              <Fields
                id="Role"
                label="Role *"
                names={[
                  'Role Name',
                  'Role',
                ]}
                autosuggestFieldName="Role Name"
                fieldArrayName="Role"
                placeholder="-Select Role-"
                errorPlacement="left"
                component={MultiselectAutosuggestField}
                renderSuggestionContent={suggestion => suggestion.Name}
                renderInputContent={suggestion => suggestion.Name}
                onSuggestionSelected={(suggestion) => {
                  change('Selected Role', {
                    value: suggestion.ID,
                    label: suggestion.Name,
                    accesses: suggestion.Accesses.map(item => item.ID),
                  });
                  return '';
                }}
                fetchSuggestions={({ value, limit, offset }) => (
                  value.length > 0
                    || account.Roles.filter(item => item.Name === 'Master Administrator').length === 0
                    || selectedRolesID.length > 0
                    ? requestSearchRoles({
                      filters: [
                        ...value.length > 0
                          ? [{
                            name: 'Name',
                            operation: 'startswith',
                            val: value,
                            case_sensitivity: false,
                          }] : [],
                        ...selectedRolesID.length > 0
                          ? selectedRolesID.map(item => ({
                            name: 'ID',
                            operation: 'ne',
                            val: item,
                          })) : [],
                        ...account.Roles.filter(item => item.Name === 'Master Administrator').length === 0
                          ? [{
                            name: 'Name',
                            operation: 'ne',
                            val: 'Master Administrator',
                          }] : [],
                      ],
                      limit,
                      offset,
                    }).then(res => res.data,
                      (err) => {
                        if (err.data.error === 'Need authorisation') {
                          actions.accountLoginFail();
                        }
                        return err;
                      })
                    : requestFetchRoles({
                      limit,
                      offset,
                    }).then(res => res.data,
                      (err) => {
                        if (err.data.error === 'Need authorisation') {
                          actions.accountLoginFail();
                        }
                        return err;
                      })
                )}
                keepOpenAfterSelection
              />
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            justify="flex-start"
            wrap="nowrap"
            spacing={24}
          >
            <Grid item xs={12}>
              {groupedAccessPermissions.map(item => (
                <UserCreateEditFormSection
                  key={item.Group}
                  {...item}
                />
              ))}
            </Grid>
          </Grid>
          {initialValues.ID &&
            <Grid
              className={classes.grid}
              container
              justify="flex-start"
              alignItems="flex-end"
              wrap="nowrap"
              spacing={24}
            >
              <Grid item xs={7} md={6}>
                <Fields
                  id="Activity Log From / To"
                  names={[
                    'Activity Log From',
                    'Activity Log To',
                  ]}
                  label="Generate User Actions Activity Log From / To"
                  component={DatepickerField}
                  validate={[validate.date]}
                  normalize={{
                    'Activity Log From': normalize.date,
                    'Activity Log To': normalize.date,
                  }}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName="Activity Log From"
                  endDateName="Activity Log To"
                  alwaysPrefillEndDate
                  inputAdornmentLgOnly
                  rangeButtons
                />
              </Grid>
              <Grid item xs={5} md={6}>
                <Button
                  variant="raised"
                  color="primary"
                  disabled={submitting}
                  onClick={() => actions.generateUserActivityLog({
                    'Date of Creation From': formValues['Activity Log From'],
                    'Date of Creation To': formValues['Activity Log To'],
                  })}
                >
                  Download
                </Button>
              </Grid>
            </Grid>
          }
        </form>
      </SectionBody>
    </Section>
  );
};

UserCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
  actions: PropTypes.shape({
    accountResetPassword: PropTypes.func.isRequired,
    accountLoginFail: PropTypes.func.isRequired,
    generateUserActivityLog: PropTypes.func.isRequired,
  }).isRequired,
  initialValues: PropTypes.shape({
    Email: PropTypes.string,
  }).isRequired,
  formValues: PropTypes.shape({
    Email: PropTypes.string,
  }),
  change: PropTypes.func.isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  accessPermissions: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    gridItemRecoveryLink: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
  }).isRequired,
};

UserCreateEditForm.defaultProps = {
  error: null,
  formValues: {},
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    accountResetPassword,
    accountLoginFail,
    generateUserActivityLog,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({
  onSubmit,
  onChange,
  validate: validateForm,
  initialValues: {
    Gender: 'Male',
    'Access Level': '1',
    Active: 'true',
  },
})(withStyles(styles)(UserCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/UserCreateEditForm/UserCreateEditForm.js